"use client";

/**
 * Small inline SVG flags for the currency UX (welcome modal + header switch).
 *
 * We deliberately do NOT use flag emoji: the regional-indicator emoji do not
 * render as flags on Windows, so they would show as bare two-letter boxes.
 * These are simple, crisp, self-contained SVGs sized by a `size` prop.
 */

export function CanadaFlag({ size = 18 }: { size?: number }) {
  // Simplified Canadian flag: red bars left and right, white centre, red leaf.
  return (
    <svg
      width={size}
      height={size * 0.66}
      viewBox="0 0 32 21"
      role="img"
      aria-label="Canada"
      style={{ borderRadius: 2, display: "block", flexShrink: 0 }}
    >
      <rect width="32" height="21" fill="#fff" />
      <rect width="8" height="21" x="0" fill="#d52b1e" />
      <rect width="8" height="21" x="24" fill="#d52b1e" />
      <path
        fill="#d52b1e"
        d="M16 4.2l.9 1.9 2-.5-.9 1.9 1.6.3-2 1.5.4 1.1-2-.4v2.1h-.9v-2.1l-2 .4.4-1.1-2-1.5 1.6-.3-.9-1.9 2 .5z"
      />
    </svg>
  );
}

export function USFlag({ size = 18 }: { size?: number }) {
  // Simplified US flag: red/white stripes with a blue canton of stars.
  return (
    <svg
      width={size}
      height={size * 0.66}
      viewBox="0 0 32 21"
      role="img"
      aria-label="United States"
      style={{ borderRadius: 2, display: "block", flexShrink: 0 }}
    >
      <rect width="32" height="21" fill="#fff" />
      {/* 7 red stripes across the full width */}
      {[0, 2, 4, 6, 8, 10, 12].map((i) => (
        <rect key={i} y={(i * 21) / 13} width="32" height={21 / 13} fill="#b22234" />
      ))}
      {/* Blue canton */}
      <rect width="14" height={(21 / 13) * 7} fill="#3c3b6e" />
      {/* A few simplified stars */}
      <g fill="#fff">
        {[2.5, 6, 9.5].map((x) =>
          [2, 5.5, 9].map((y) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r="0.7" />
          ))
        )}
      </g>
    </svg>
  );
}

/** Pick the flag for a currency. USD -> US flag, CAD -> Canada flag. */
export function CurrencyFlag({
  currency,
  size = 18,
}: {
  currency: "USD" | "CAD";
  size?: number;
}) {
  return currency === "CAD" ? <CanadaFlag size={size} /> : <USFlag size={size} />;
}
